<template>
  <div class="grid-lineups grid-lineup-changes">
    <filterBar
      @updateFilters="updateFilters(...arguments)"
      @updateDateFilters="updateDateFilters(...arguments)"
      :client="selectedClient"
      :fiduciary="selectedFiduciary"
      :hideFilters="hideFilters"
      @updateProductFunds="updateProduct"
    />
    <div class="grid-lineups__container">
      <mds-loader v-if="loading" />
      <div v-else class="grid-lineups__wrapper">
        <div v-if="products.length" class="grid-lineups__table">
          <div
            v-for="(product, productRow) in filteredProducts"
            :key="product.productId"
            class="grid-lineups__product"
          >
            <h3 class="grid-lineups__name">
              <CollapseButton
                :title="'Open/Close Product Funds'"
                :collapsed="product.__hidden"
                @toggle="toggleProductFunds(productRow)"
              >
                <span>{{ product.productName }} ({{ product.productCode }})</span>
              </CollapseButton>
            </h3>

            <div class="mds-site-navigation" v-show="!product.__hidden">
              <ul class="mds-site-navigation__list">
                <li
                  v-for="(menu, menuIndex) in product.menus"
                  :key="`${product.productId}-${menu.menuId}`"
                  :class="{
                    'mds-site-navigation__list-item--active': menuIndex === product.selectedTab,
                  }"
                  class="mds-site-navigation__list-item"
                >
                  <div class="mds-site-navigation__list-item-inner">
                    <a
                      @click="selectTab(product.index, menuIndex)"
                      class="mds-button mds-button--flat mds-site-navigation__button"
                    >
                      {{ `${product.program.programName} ${menu.menuName}` }}
                      <span v-if="menu.lineupPendingForApproval" class="pending-icon mds-icon">
                        <base-grid-icon
                          class="mds-icon"
                          aria-hidden="true"
                          icon="clock-back"
                          tooltipMessage="Pending"
                          @showToolTip="showTooltip"
                          @hideToolTip="hideTooltip"
                        />
                      </span>
                    </a>
                  </div>
                </li>
              </ul>
            </div>
            <div class="grid-lineups__table" v-show="!product.__hidden">
              <div
              v-if="product.menus[product.selectedTab].fundsToShow && product.menus[product.selectedTab].fundsToShow.length"
              >
                <mds-table row-hover
                  :key="`mds-data-table-${product.productId}-${product.selectedTab}`"
                >
                  <mds-thead>
                    <mds-th
                      v-for="(header, index) in columnsConfig"
                      :key="index"
                      :style="header.style"
                    >
                      {{ header.text }}
                    </mds-th>
                  </mds-thead>
                  <mds-tbody>
                    <mds-tr v-for="(row, index) in product.menus[product.selectedTab].fundsToShow"
                      :key="index"
                    >
                      <mds-td v-for="(header, i) in columnsConfig"
                        :key="i"
                        :style="header.style"
                      >
                        <template v-if="header.fieldName === 'dateOfChange'">
                          {{ formatDate(row.dateOfChange) || "—" }}
                        </template>
                        <template v-else-if="header.fieldName === 'ticker'">
                          {{ row.ticker || "—" }}
                        </template>
                        <template v-else>
                          {{ row[header.fieldName] }}
                        </template>
                      </mds-td>
                    </mds-tr>
                  </mds-tbody>
                </mds-table>
                <!-- eslint-disable max-len -->
                <mds-pagination show-items-info show-items-select
                  :total-items="product.menus[product.selectedTab].paginationConfig.totalItems"
                  @mds-pagination-page-changed="handleTablePageChange($event, product.productId, product.menus[product.selectedTab].menuId)"
                >
                </mds-pagination>
              </div>
              <p v-else class="empty-state">No data found.</p>
            </div>
          </div>
        </div>
        <p v-else class="empty-state">No lineups available.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  cloneDeep,
  get,
  merge,
  sortBy,
} from 'lodash';
import {
  MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd,
} from '@mds/data-table';
import MdsPagination from '@mds/pagination';
import axios from 'axios';
import BaseGridIcon from '@/components/atoms/BaseGridIcon';
import CollapseButton from '@/components/atoms/CollapseButton';
import FilterBar from '@/components/organisms/FilterBar';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import GridFormatDataMixin from '@/mixins/GridFormatDataMixin';

import * as LineupsFundsService from '@/services/LineupsFunds';

export default {
  name: 'grid-lineups',
  components: {
    BaseGridIcon,
    CollapseButton,
    FilterBar,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsPagination,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Templates/GridLineups' }),
    GridFormatDataMixin({ withGlobalState: true }),
  ],
  data() {
    return {
      loading: true,
      requestController: null,
      abortRequestKey: 'AbortRequest',
      request: null,
      requestUrl: '',
      products: [],
      currentFilters: {
        assets: [],
        categories: [],
        status: [],
        updates: [],
        dateRange: {
          startDate: '',
          endDate: '',
        },
      },
      allStatusMode: false,
      columnsConfig: [
        {
          text: 'Fund Name',
          fieldName: 'displayName',
          align: 'left',
          sortDirection: 1,
        },
        {
          text: 'Ticker',
          fieldName: 'ticker',
          align: 'left',
          style: {
            width: '124px',
          },
          sortDirection: 0,
        },
        {
          text: 'Sec ID',
          fieldName: 'secID',
          align: 'left',
          style: {
            width: '124px',
          },
          sortDirection: 0,
        },
        {
          text: 'Fiduciary Category',
          fieldName: 'categoryName',
          align: 'left',
          style: {
            width: '196px',
          },
          sortDirection: 0,
        },
        {
          text: 'Updates',
          fieldName: 'action',
          align: 'left',
          style: {
            width: '76px',
          },
          sortDirection: 0,
        },
        {
          text: 'Date of Change',
          fieldName: 'dateOfChange',
          align: 'left',
          style: {
            width: '120px',
          },
          sortDirection: 0,
        },
      ],
      maxRowsTable: 10,
      searchFilterApplied: false,
      filteredRecords: [],
    };
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      selectedProduct: (state) => state.grid.product.selected,
      selectedProductName: (state) => state.grid.product.name,
      selectedProductCode: (state) => state.grid.product.code,
      searchTerm: (state) => state.grid.searchTerm,
      selectedTab: (state) => state.grid.tab,
    }),
    hideFilters() {
      return {
        products: false,
        classes: true,
        categories: false,
        statuses: true,
        updates: false,
        dateRange: false,
        reviewStatuses: true,
      };
    },
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`);
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    filteredProducts() {
      /* eslint-disable no-param-reassign */
      const products = cloneDeep(this.products);

      try {
        const filteredProducts = products
          .filter((product) => {
            if (this.selectedProduct) {
              return product.productCode === this.selectedProductCode;
            }
            return true;
          })
          .map((product) => {
            const { menus = [] } = product;
            product.menus = menus.map((menu) => {
              const { funds = [] } = menu;
              menu.funds = funds
                .map((fund) => {
                  let {
                    psaCategory = [],
                    /* eslint-disable-next-line prefer-const */
                    fundStatus: fundStatuData,
                  } = fund;
                  if (psaCategory === null) {
                    psaCategory = [{ category: { name: '', broadAssetClassName: '' } }];
                  }
                  const [firstCategory = {}] = psaCategory;
                  const { category } = firstCategory || {};
                  const { name: categoryName = '', broadAssetClassName: assetName = '' } = category || {};

                  const fundStatusObj = this.getSafeFundStatusObj(fundStatuData);
                  const { fundStatus: { name: statusName = '' } = {} } = fundStatusObj || {};

                  fund.fundStatus = [{ name: statusName }];
                  if (this.isTargetSeries(fund)) {
                    const pendingFundStatus = this.formatPendingStatuses(fund);
                    const fundActualStatus = this.formatFundStatuses(fund);
                    fund.fundStatus = this.formatTargetSeriesStatus(fund, pendingFundStatus, fundActualStatus, false);
                    fund.isPending = Array.isArray(pendingFundStatus) && pendingFundStatus.length > 0;
                  }
                  fund.categoryName = categoryName;
                  fund.assetName = assetName;

                  return fund;
                })
                .filter((fund) => {
                  const filter = (this.currentFilters.categories.length
                    ? this.currentFilters.categories.indexOf(fund.categoryName) !== -1
                    : true)
                    && (this.currentFilters.assets.length
                      ? this.currentFilters.assets.indexOf(fund.assetName) !== -1
                      : true)
                    && (this.currentFilters.updates.length
                      ? this.currentFilters.updates.indexOf(fund.action) !== -1
                      : true)
                    && (this.currentFilters.status.length
                      ? this.currentFilters.status.indexOf(fund.statusName) !== -1
                      : true);

                  const matchName = new RegExp(this.searchTerm, 'gi').test(fund.displayName);
                  const matchTicker = new RegExp(this.searchTerm, 'gi').test(fund.ticker);
                  const matchSecID = new RegExp(this.searchTerm, 'gi').test(fund.secID);

                  const search = matchName || matchTicker || matchSecID;
                  return filter && search;
                });
              if (this.searchFilterApplied) {
                menu.paginationConfig = this.showPaginationConfig(menu.funds);
              }
              /* eslint-disable-next-line */
              menu.fundsToShow = this.convertArrayToChunks(menu.funds, menu.paginationConfig.pageSize)[+menu.paginationConfig.page - 1];
              return menu;
            });
            return product;
          });
        /* eslint-enable no-param-reassign */
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.searchFilterApplied = false;
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.filteredRecords = filteredProducts;
        return filteredProducts;
      } catch (error) {
        this.logError({
          data: products,
          errorFrom: 'GridLineup:computed:filteredProducts',
          details: {
            ...Object.getOwnPropertyDescriptors(error),
            message: error.message,
          },
        });
      }
      // If an error occurred
      return this.products;
    },
  },
  created() {
    this._load();
  },
  beforeDestroy() {
    this.requestController && this.requestController.cancel(this.abortRequestKey);
  },
  methods: {
    _load(dateFilter = false) {
      if (this.requestController) {
        this.requestController.cancel();
      }
      /* eslint-disable no-param-reassign */
      this.requestController = axios.CancelToken.source();
      this.loading = true;
      const apiParams = {
        selectedClient: this.selectedClient,
        axiosCancelToken: this.requestController?.token,
      };
      if (dateFilter) {
        apiParams.dateFilter = this.currentFilters.dateRange;
      }
      if (+this.selectedClient !== 0) {
        LineupsFundsService.getLineupChangesList(apiParams).then((result) => {
          if (result.status.success) {
            const { data = {} } = result;
            const { products = [] } = data;

            this.products = products.map((product, index) => {
              const { menus = [] } = product;
              product.selectedTab = 0;
              product.__hidden = false;
              product.index = index;
              product.menus = menus.map((menu) => {
                let { funds = [] } = menu;
                // Sort Menu Funds by displayName
                funds = sortBy(funds, 'displayName');

                menu.funds = funds.map((fund) => {
                  // Update PendingStatus
                  const { fundStatus, pendingStatus } = fund;
                  if (pendingStatus) {
                    fund.fundStatus = merge(fundStatus, pendingStatus);
                  }
                  return fund;
                });
                // eslint-disable-next-line prefer-destructuring
                menu.fundsToShow = this.convertArrayToChunks(menu.funds, this.maxRowsTable)[0];
                menu.paginationConfig = this.showPaginationConfig(menu.funds);
                return menu;
              });
              return product;
            });
          }
          this.loading = false;
        }).catch((error) => {
          const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
          if (httpStatus === 401) {
            this.$router.push('/login');
          } else if (error.message && error.message !== this.abortRequestKey) {
            this.notifyError({
              error,
              section: 'methods:loadData',
              data: { url: this.requestUrl },
              notificaction: {
                title: 'Lineups Grid',
                message: `Fetching Data (Error: ${error.message})`,
              },
            });
          }
          this.loading = false;
        }).finally(() => {
          this.cleanRequest();
        });
      } else {
        this.loading = false;
      }
      /* eslint-enable no-param-reassign */
    },
    clearClientSideFilters() {
      this.currentFilters.assets = [];
      this.currentFilters.categories = [];
      this.currentFilters.status = [];
      this.currentFilters.updates = [];
    },
    convertArrayToChunks(input, size) {
      if (size === -1) { return [input]; }
      return input.reduce((array, item, index) => {
        return index % size === 0
          ? [...array, [item]]
          : [...array.slice(0, -1), [...array.slice(-1)[0], item]];
      }, []);
    },
    handleTablePageChange(pageConfig, productId, menuId) {
      const products = cloneDeep(this.filteredRecords);
      products.map((product, productIndex) => {
        const modifProduct = cloneDeep(product);
        if (modifProduct.productId === productId) {
          modifProduct.menus = modifProduct.menus.map((menuItem, menuIndex) => {
            const modifMenu = cloneDeep(menuItem);
            if (modifMenu.menuId === menuId) {
              /* eslint-disable max-len */
              this.products[productIndex].menus[menuIndex].paginationConfig = { ...modifMenu.paginationConfig, ...pageConfig };
            }
            return modifMenu;
          });
        }
        return modifProduct;
      });
    },
    showPaginationConfig(rows) {
      return {
        page: 1,
        pageSize: 10,
        showItemsInfo: true,
        showItemsSelect: true,
        totalItems: rows.length,
        pageSizes: [10, 20, 50, -1],
      };
    },
    updateDateFilters(filters) {
      if (filters.dateRange?.startDate !== '') {
        this.clearClientSideFilters();
        if (filters.dateRange?.endDate !== '') {
          this._load(true);
        }
      }
      if (filters.dateRange?.startDate === '' && filters.dateRange?.endDate === '') {
        this._load(false);
      }
    },
    updateFilters(filters, allStatus) {
      this.currentFilters.assets = filters.assets;
      this.currentFilters.categories = filters.categories;
      this.currentFilters.status = filters.status;
      this.currentFilters.updates = filters.updates;
      this.currentFilters.dateRange.startDate = filters.dateRange?.startDate;
      this.currentFilters.dateRange.endDate = filters.dateRange?.endDate;
      this.allStatusMode = allStatus;
      this.searchFilterApplied = true;
    },
    updateProduct(product, productName, productCode) {
      this.$store.dispatch('setProduct', {
        code: productCode,
        listVisible: false,
        name: productName,
        selected: product,
      });
    },
    showTooltip(...args) {
      this.$emit('showTooltip', ...args);
    },
    hideTooltip() {
      this.$emit('hideTooltip');
    },
    selectTab(productIndex, menuIndex) {
      this.products[productIndex].selectedTab = menuIndex;
    },
    toggleProductFunds(productIndex) {
      this.products = this.products.map((product) => {
        const modifProduct = cloneDeep(product);
        if (modifProduct.index === productIndex) {
          modifProduct.__hidden = !modifProduct.__hidden;
        }
        return modifProduct;
      });
    },
    formatDate(dateValue) {
      return new Date(dateValue).toLocaleString('en-us', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    cleanRequest() {
      this.request = null;
      this.requestController = null;
    },
    getSafeFundStatusObj(fundStatusData) {
      if (fundStatusData instanceof String) {
        return { fundStatus: { name: fundStatusData } };
      }
      if (Array.isArray(fundStatusData) && fundStatusData.length > 0) {
        return fundStatusData[0];
      }
      if (!fundStatusData) {
        return undefined;
      }
      return fundStatusData;
    },
  },
  watch: {
    selectedClient() {
      this._load();
    },
    searchTerm() {
      this.searchFilterApplied = true;
    },
  },
};
</script>
<style lang="scss">
.grid-lineups {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0; // Firefox Flexbox fix

  &__container {
    height: 100%;
    overflow-y: auto;
  }

  &__product {
    h3 {
      @include mds-level-3-heading();
      margin-bottom: 0;
    }

    &:last-child {
      margin-bottom: $mds-space-inset-8-x;
    }
  }

  .mds-site-navigation {
    margin-bottom: $mds-space-3-x;
    &__list {
      justify-content: flex-start;
    }
    &__list-item {
      margin: 0;
    }
    &__list-item--active {
      .mds-site-navigation__list-item-inner {
        box-shadow: inset 0 -6px 0 0 #061c25;
      }
    }
    .pending-icon {
      padding-left: $mds-space-1-x;
    }
  }

  &__fund-status {
    padding-left: $mds-space-inset-1-x;
  }

  &__fund-pending {
    padding-left: $mds-space-inset-2-x;
  }

  &__table {
    padding-left: $mds-space-2-x;

    table {
      margin-bottom: $mds-space-2-x;
    }
  }

  .empty-state {
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    padding-left: $mds-space-2-x;
  }
}
.grid-lineups.grid-lineup-changes {
  @media screen and (max-height: 750px) {
    .single-list, .multiple-list {
      max-height: 130px;
    }
  }
}
</style>
